import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { disputeService } from '../../../services/disputeService';
import { useAuth } from '../../../contexts/AuthContext';
import DashboardSkeleton from '../../../components/ui/DashboardSkeleton';
import ErrorFallback from '../../../components/ui/ErrorFallback';

const DisputeTracking = () => {
  const { user, userProfile } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filter, setFilter] = useState('open');
  const [adminMessage, setAdminMessage] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionAction, setResolutionAction] = useState(null);
  const [settlementAmount, setSettlementAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && userProfile?.role === 'admin') {
      loadDisputes();
    }
  }, [user, userProfile]);

  const loadDisputes = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await disputeService?.getAdminDisputes();
      setDisputes(data || []);

      // Setup real-time subscription
      const channel = disputeService?.subscribeToAdminDisputes((payload) => {
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
          loadDisputes();
        }
      });

      return () => {
        if (channel) {
          disputeService?.unsubscribe(channel);
        }
      };
    } catch (err) {
      console.error('Error loading disputes:', err);
      setError(err?.message || 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'open':
        return { label: 'Open', color: 'bg-warning/10 text-warning', icon: 'AlertCircle' };
      case 'under_review':
        return { label: 'Under Review', color: 'bg-primary/10 text-primary', icon: 'Eye' };
      case 'escalated':
        return { label: 'Escalated', color: 'bg-error/10 text-error', icon: 'AlertTriangle' };
      case 'resolved':
        return { label: 'Resolved', color: 'bg-success/10 text-success', icon: 'CheckCircle' };
      default:
        return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: 'HelpCircle' };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high':
        return { label: 'High', color: 'text-error' };
      case 'medium':
        return { label: 'Medium', color: 'text-warning' };
      case 'low':
        return { label: 'Low', color: 'text-success' };
      default:
        return { label: 'Normal', color: 'text-muted-foreground' };
    }
  };

  const handleSendMessage = () => {
    if (!adminMessage?.trim() || !selectedDispute) return;

    const newMessage = {
      id: selectedDispute?.messages?.length + 1,
      sender: 'admin',
      senderName: 'Platform Admin',
      message: adminMessage,
      timestamp: new Date()?.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }),
      attachments: []
    };

    setDisputes((prev) =>
    prev?.map((d) =>
    d?.id === selectedDispute?.id ?
    { ...d, messages: [...d?.messages, newMessage], lastUpdate: new Date()?.toISOString()?.split('T')?.[0] } :
    d
    )
    );

    setSelectedDispute((prev) => ({
      ...prev,
      messages: [...prev?.messages, newMessage]
    }));

    setAdminMessage('');
  };

  const handleResolve = (action) => {
    setResolutionAction(action);
    setShowResolutionModal(true);
  };

  const confirmResolution = async () => {
    if (!resolutionNote?.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    try {
      setSubmitting(true);

      await disputeService?.applySettlementDecision(selectedDispute?.id, {
        adminDecision: resolutionAction,
        responseType: resolutionAction === 'partial_settlement' ? 'partial_settlement' : 'accept',
        responseMessage: resolutionNote,
        settlementAmount: resolutionAction === 'partial_settlement' ? parseFloat(settlementAmount) : null,
        settlementNotes: resolutionNote
      });

      setShowResolutionModal(false);
      setResolutionNote('');
      setSettlementAmount('');
      setSelectedDispute(null);
      
      // Reload disputes
      await loadDisputes();
    } catch (err) {
      console.error('Error applying settlement:', err);
      alert('Failed to apply settlement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEscalate = (disputeId) => {
    setDisputes((prev) =>
    prev?.map((d) => d?.id === disputeId ? { ...d, status: 'escalated', priority: 'high' } : d)
    );
  };

  const handleChangeStatus = (disputeId, newStatus) => {
    setDisputes((prev) =>
    prev?.map((d) => d?.id === disputeId ? { ...d, status: newStatus } : d)
    );
  };

  const filteredDisputes = disputes?.filter((d) => {
    if (filter === 'all') return true;
    // Map charge_status to filter values
    if (filter === 'open') return d?.chargeStatus === 'disputed';
    return false;
  });

  const openCount = disputes?.filter((d) => d?.chargeStatus === 'disputed')?.length;
  const resolvedCount = disputes?.filter((d) => d?.chargeStatus !== 'disputed')?.length;
  const underReviewCount = disputes?.filter((d) => d?.status === 'under_review')?.length;
  const escalatedCount = disputes?.filter((d) => d?.status === 'escalated')?.length;

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={loadDisputes} />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="AlertCircle" size={20} strokeWidth={2} className="text-warning" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{openCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Open Disputes</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="Eye" size={20} strokeWidth={2} className="text-primary" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{underReviewCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Under Review</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="AlertTriangle" size={20} strokeWidth={2} className="text-error" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{escalatedCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Escalated Cases</p>
        </div>
        <div className="bg-card rounded-lg shadow-elevation-2 p-5">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center mb-3">
            <Icon name="CheckCircle" size={20} strokeWidth={2} className="text-success" />
          </div>
          <p className="font-heading text-2xl font-bold text-foreground mb-1">{resolvedCount}</p>
          <p className="font-caption text-sm text-muted-foreground">Resolved</p>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-2">
        <div className="flex space-x-2 overflow-x-auto">
          {['all', 'open', 'under_review', 'escalated', 'resolved']?.map((status) =>
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth capitalize whitespace-nowrap ${
            filter === status ?
            'bg-primary text-primary-foreground' :
            'text-muted-foreground hover:bg-muted'}`
            }>
            
              {status?.replace('_', ' ')}
            </button>
          )}
        </div>
      </div>
      {/* Disputes List */}
      <div className="bg-card rounded-lg shadow-elevation-2">
        <div className="p-6 border-b border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Scale" size={20} strokeWidth={2} className="text-primary" />
            <span>Active Dispute Cases</span>
          </h3>
        </div>
        <div className="divide-y divide-border">
          {filteredDisputes?.length === 0 ?
          <div className="p-8 text-center">
              <Icon name="Inbox" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">No disputes found</p>
            </div> :

          filteredDisputes?.map((dispute) => {
            const statusConfig = getStatusConfig(dispute?.status);
            const priorityConfig = getPriorityConfig(dispute?.priority);
            return (
              <div key={dispute?.id} className="p-5 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2 flex-wrap">
                        <h4 className="font-body font-semibold text-foreground">{dispute?.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.color} flex items-center space-x-1`}>
                          <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                          <span>{statusConfig?.label}</span>
                        </span>
                        <span className={`text-xs font-medium ${priorityConfig?.color}`}>
                          {priorityConfig?.label} Priority
                        </span>
                      </div>
                      <p className="font-body text-sm text-muted-foreground mb-2">{dispute?.caseNumber}</p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-2 flex-wrap">
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>Owner: {dispute?.owner}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="User" size={12} strokeWidth={2} />
                          <span>Renter: {dispute?.renter}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="Truck" size={12} strokeWidth={2} />
                          <span>{dispute?.trailerName}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Icon name="DollarSign" size={12} strokeWidth={2} />
                          <span>${dispute?.amount}</span>
                        </span>
                      </div>
                      <p className="font-body text-sm text-foreground">{dispute?.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setSelectedDispute(dispute)}>
                      
                        <Icon name="Eye" size={14} strokeWidth={2} />
                        <span className="ml-1">Review</span>
                      </Button>
                    </div>
                  </div>
                </div>);

          })
          }
        </div>
      </div>
      {/* Detailed Dispute Modal */}
      {selectedDispute &&
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-elevation-5 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div>
                <h3 className="font-heading text-xl font-semibold text-foreground">{selectedDispute?.title}</h3>
                <p className="font-body text-sm text-muted-foreground">{selectedDispute?.caseNumber}</p>
              </div>
              <button
              onClick={() => setSelectedDispute(null)}
              className="p-2 hover:bg-muted rounded-md transition-smooth">
              
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Case Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4 space-y-3">
                  <h4 className="font-body font-semibold text-foreground flex items-center space-x-2">
                    <Icon name="FileText" size={16} strokeWidth={2} className="text-primary" />
                    <span>Case Details</span>
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusConfig(selectedDispute?.status)?.color} flex items-center space-x-1`}>
                        <Icon name={getStatusConfig(selectedDispute?.status)?.icon} size={12} strokeWidth={2} />
                        <span>{getStatusConfig(selectedDispute?.status)?.label}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Priority:</span>
                      <span className={`font-body text-sm font-medium ${getPriorityConfig(selectedDispute?.priority)?.color}`}>
                        {getPriorityConfig(selectedDispute?.priority)?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Amount:</span>
                      <span className="font-body text-sm font-semibold">${selectedDispute?.amount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Category:</span>
                      <span className="font-body text-sm capitalize">{selectedDispute?.category?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Created:</span>
                      <span className="font-body text-sm">{selectedDispute?.createdDate}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Last Update:</span>
                      <span className="font-body text-sm">{selectedDispute?.lastUpdate}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-muted rounded-lg p-4 space-y-3">
                  <h4 className="font-body font-semibold text-foreground flex items-center space-x-2">
                    <Icon name="Users" size={16} strokeWidth={2} className="text-primary" />
                    <span>Parties Involved</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="font-body text-xs text-muted-foreground mb-1">Owner</p>
                      <p className="font-body text-sm font-medium">{selectedDispute?.owner}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-foreground mb-1">Renter</p>
                      <p className="font-body text-sm font-medium">{selectedDispute?.renter}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-foreground mb-1">Trailer</p>
                      <div className="flex items-center space-x-2">
                        <img
                        src={selectedDispute?.trailerImage}
                        alt={selectedDispute?.trailerImageAlt}
                        className="w-12 h-12 rounded object-cover" />
                      
                        <p className="font-body text-sm font-medium">{selectedDispute?.trailerName}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-body font-semibold text-foreground mb-2">Description</h4>
                <p className="font-body text-sm text-foreground">{selectedDispute?.description}</p>
              </div>

              {/* Evidence */}
              {selectedDispute?.evidence?.length > 0 &&
            <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="Paperclip" size={16} strokeWidth={2} className="text-primary" />
                    <span>Evidence ({selectedDispute?.evidence?.length})</span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedDispute?.evidence?.map((item) =>
                <div key={item?.id} className="bg-card rounded-lg p-3 border border-border">
                        {item?.type === 'image' ?
                  <img
                    src={item?.url}
                    alt={item?.alt}
                    className="w-full h-24 object-cover rounded mb-2" /> :


                  <div className="w-full h-24 bg-primary/10 rounded mb-2 flex items-center justify-center">
                            <Icon name="FileText" size={32} strokeWidth={1.5} className="text-primary" />
                          </div>
                  }
                        <p className="font-body text-xs text-foreground truncate">{item?.name}</p>
                      </div>
                )}
                  </div>
                </div>
            }

              {/* Timeline */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="Clock" size={16} strokeWidth={2} className="text-primary" />
                  <span>Case Timeline</span>
                </h4>
                <div className="space-y-3">
                  {selectedDispute?.timeline?.map((event, index) =>
                <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-body text-sm font-medium text-foreground">{event?.action}</p>
                          <p className="font-body text-xs text-muted-foreground">{event?.date}</p>
                        </div>
                        <p className="font-body text-xs text-muted-foreground">{event?.actor}</p>
                        <p className="font-body text-sm text-foreground mt-1">{event?.description}</p>
                      </div>
                    </div>
                )}
                </div>
              </div>

              {/* Communication Log */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="MessageSquare" size={16} strokeWidth={2} className="text-primary" />
                  <span>Communication Log</span>
                </h4>
                <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                  {selectedDispute?.messages?.map((msg) =>
                <div
                  key={msg?.id}
                  className={`p-3 rounded-lg ${
                  msg?.sender === 'admin' ? 'bg-primary/10 border border-primary/20' : 'bg-card border border-border'}`
                  }>
                  
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      msg?.sender === 'admin' ? 'bg-primary/20' :
                      msg?.sender === 'owner' ? 'bg-warning/20' : 'bg-success/20'}`
                      }>
                            <Icon
                          name={msg?.sender === 'admin' ? 'Shield' : 'User'}
                          size={14}
                          strokeWidth={2}
                          className={msg?.sender === 'admin' ? 'text-primary' : 'text-foreground'} />
                        
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium capitalize">{msg?.senderName}</p>
                            <p className="font-body text-xs text-muted-foreground capitalize">({msg?.sender})</p>
                          </div>
                        </div>
                        <span className="font-body text-xs text-muted-foreground">{msg?.timestamp}</span>
                      </div>
                      <p className="font-body text-sm text-foreground">{msg?.message}</p>
                      {msg?.attachments?.length > 0 &&
                  <div className="mt-2 flex items-center space-x-2">
                          <Icon name="Paperclip" size={12} strokeWidth={2} className="text-muted-foreground" />
                          <p className="font-body text-xs text-muted-foreground">
                            {msg?.attachments?.length} attachment(s)
                          </p>
                        </div>
                  }
                    </div>
                )}
                </div>

                {/* Admin Message Input */}
                {selectedDispute?.status !== 'resolved' &&
              <div className="space-y-2">
                    <div className="flex items-start space-x-2">
                      <Input
                    type="text"
                    placeholder="Type admin message..."
                    value={adminMessage}
                    onChange={(e) => setAdminMessage(e?.target?.value)}
                    onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                    className="flex-1" />
                  
                      <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    disabled={!adminMessage?.trim()}>
                    
                        <Icon name="Send" size={16} strokeWidth={2} />
                        <span className="ml-1">Send</span>
                      </Button>
                    </div>
                  </div>
              }
              </div>

              {/* Resolution Section */}
              {selectedDispute?.status === 'resolved' && selectedDispute?.resolution ?
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <h4 className="font-body font-semibold text-success mb-3 flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} strokeWidth={2} />
                    <span>Case Resolved</span>
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Resolution Action:</span>
                      <span className="font-body text-sm font-medium capitalize">
                        {selectedDispute?.resolution?.action?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Resolved By:</span>
                      <span className="font-body text-sm font-medium">{selectedDispute?.resolution?.resolvedBy}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Resolved Date:</span>
                      <span className="font-body text-sm">{selectedDispute?.resolution?.resolvedDate}</span>
                    </div>
                    <div className="pt-2 border-t border-success/20">
                      <p className="font-body text-sm text-foreground">{selectedDispute?.resolution?.note}</p>
                    </div>
                  </div>
                </div> :

            <div className="bg-card border border-border rounded-lg p-4">
                  <h4 className="font-body font-semibold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="Gavel" size={16} strokeWidth={2} className="text-primary" />
                    <span>Resolution Controls</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Button
                  variant="success"
                  fullWidth
                  onClick={() => handleResolve('favor_owner')}>
                  
                      <Icon name="CheckCircle" size={16} strokeWidth={2} />
                      <span className="ml-2">Resolve - Favor Owner</span>
                    </Button>
                    <Button
                  variant="success"
                  fullWidth
                  onClick={() => handleResolve('favor_renter')}>
                  
                      <Icon name="CheckCircle" size={16} strokeWidth={2} />
                      <span className="ml-2">Resolve - Favor Renter</span>
                    </Button>
                    <Button
                  variant="primary"
                  fullWidth
                  onClick={() => handleResolve('partial_refund')}>
                  
                      <Icon name="DollarSign" size={16} strokeWidth={2} />
                      <span className="ml-2">Partial Refund</span>
                    </Button>
                    <Button
                  variant="primary"
                  fullWidth
                  onClick={() => handleResolve('mediation')}>
                  
                      <Icon name="Users" size={16} strokeWidth={2} />
                      <span className="ml-2">Arrange Mediation</span>
                    </Button>
                    {selectedDispute?.status !== 'escalated' &&
                <Button
                  variant="warning"
                  fullWidth
                  onClick={() => {
                    handleEscalate(selectedDispute?.id);
                    setSelectedDispute((prev) => ({ ...prev, status: 'escalated', priority: 'high' }));
                  }}>
                  
                        <Icon name="AlertTriangle" size={16} strokeWidth={2} />
                        <span className="ml-2">Escalate Case</span>
                      </Button>
                }
                    <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleChangeStatus(selectedDispute?.id, 'under_review')}>
                  
                      <Icon name="Eye" size={16} strokeWidth={2} />
                      <span className="ml-2">Move to Review</span>
                    </Button>
                  </div>
                </div>
            }
            </div>
          </div>
        </div>
      }
      {/* Resolution Modal */}
      {showResolutionModal &&
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-elevation-5 max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h3 className="font-heading text-lg font-semibold text-foreground">Confirm Resolution</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Action: <span className="font-medium capitalize">{resolutionAction?.replace('_', ' ')}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Resolution Notes *
                </label>
                <textarea
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e?.target?.value)}
                placeholder="Provide detailed notes about this resolution..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              
              </div>
              <div className="flex items-center space-x-3">
                <Button
                variant="primary"
                fullWidth
                onClick={confirmResolution}
                disabled={!resolutionNote?.trim()}>
                
                  <Icon name="CheckCircle" size={16} strokeWidth={2} />
                  <span className="ml-2">Confirm Resolution</span>
                </Button>
                <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowResolutionModal(false);
                  setResolutionNote('');
                }}>
                
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default DisputeTracking;